import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Target, Heart, GraduationCap, Sparkles, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAbVariant, getSessionMarker } from '../lib/growth';
import { eventsApi } from '../app/api';
import { useAuth } from '../app/AuthContext';
import CallFrontDeskLine from './CallFrontDeskLine';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_apex-athletes-mind/artifacts/ewmkb3wq_IMG_2112.JPG";

const audiences = [
  {
    id: 'athlete',
    icon: Target,
    label: 'Athlete',
    headline: 'Mental performance training for serious middle school, high school, and college athletes.',
    subheadline: 'The same in-person framework Coach Varsay Lewis used to help build a 2025 NJ State Championship program — now systemized for athletes, parents, and coaches. Six tools. One framework. Built to install this season, then refined in real competition.',
    cta: 'Book a Discovery Call',
  },
  {
    id: 'parent',
    icon: Heart,
    label: 'Parent',
    headline: 'Help your athlete win the mental game — without piling on.',
    subheadline: 'The conversation frameworks, post-game scripts, and mindset tools the parents of state-champion athletes use. Built by Coach Varsay Lewis, M.Ed. — install over 30 days, then refine in-season.',
    cta: 'Book a Discovery Call',
  },
  {
    id: 'coach',
    icon: GraduationCap,
    label: 'Coach',
    headline: 'Install the mental game across your roster.',
    subheadline: 'The routines, pre-game rituals, and recovery protocols that translated to a 2025 NJ State Championship. Workshops, playbooks, and consults for schools, clubs, and programs.',
    cta: 'Book a Call About Your Team',
  },
];

export const HeroWithTabs = ({ calendlyLink }) => {
  const [activeTab, setActiveTab] = useState(0);
  const currentAudience = audiences[activeTab];
  const Icon = currentAudience.icon;
  const { user } = useAuth();
  const isMember = !!user;

  // A/B test the AI Coach CTA copy
  const variant = useMemo(() => getAbVariant('hero-cta', ['A', 'B']), []);
  // aiCtaLabel was removed alongside the hero AI Coach button — A/B variant is
  // still tracked on the consult & quiz CTAs below via logCtaClick().

  const logCtaClick = (cta) => {
    eventsApi.logCtaClick({
      variant,
      cta,
      page: typeof window !== 'undefined' ? window.location.pathname : '/',
      session: getSessionMarker(),
    });
  };

  return (
    <section
      id="hero"
      className="relative min-h-[82vh] sm:min-h-[88vh] lg:min-h-[92vh] flex items-center pt-20 sm:pt-24 pb-10 sm:pb-12 overflow-hidden bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e]"
      data-testid="hero-section"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(800px_circle_at_30%_40%,rgba(0,76,84,0.15),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(600px_circle_at_70%_60%,rgba(0,76,84,0.08),transparent)]" />
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage:'linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)', backgroundSize:'60px 60px'}} />

      {/* Diagonal accent stripes — desktop only, contained to the right half so
          they echo the OG banner energy without fighting the headline copy. */}
      <div
        aria-hidden="true"
        className="hidden lg:block pointer-events-none absolute inset-y-0 right-0 w-1/2 overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.24]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(115deg, transparent 0px, transparent 70px, rgba(255,255,255,0.95) 70px, rgba(255,255,255,0.95) 73px, transparent 73px, transparent 150px, rgba(79,188,199,0.9) 150px, rgba(79,188,199,0.9) 153px, transparent 153px, transparent 230px, rgba(255,255,255,0.55) 230px, rgba(255,255,255,0.55) 232px)',
            // Soft mask: stripes fade in from the left edge and fade out at the
            // far right so the effect sits inside the hero, not on top of it.
            WebkitMaskImage:
              'linear-gradient(90deg, transparent 0%, black 22%, black 82%, transparent 100%)',
            maskImage:
              'linear-gradient(90deg, transparent 0%, black 22%, black 82%, transparent 100%)',
          }}
        />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-5 sm:px-6 w-full">
        {/* Audience Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-center mb-8 sm:mb-10"
        >
          <div className="inline-flex bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-1.5 gap-1">
            {audiences.map((audience, i) => {
              const TabIcon = audience.icon;
              return (
                <button
                  key={audience.id}
                  onClick={() => setActiveTab(i)}
                  className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    activeTab === i
                      ? 'bg-[#004C54] text-white shadow-lg'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`tab-${audience.id}`}
                >
                  <TabIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{audience.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-16 items-center">
          {/* Copy */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-[#004C54]/20 border border-[#004C54]/30 flex items-center justify-center">
                  <Icon className="h-6 w-6 text-[#4FBCC7]" />
                </div>
                <div className="font-mono-accent uppercase tracking-[0.2em] text-[11px] text-[#A5ACAF] font-semibold">
                  Mental Performance Consulting
                </div>
              </div>

              <h1 className="font-heading text-[2.5rem] sm:text-[3.2rem] lg:text-[3.8rem] font-bold tracking-tight text-white leading-[1.05] mb-5">
                {currentAudience.headline}
              </h1>

              <p className="text-[17px] sm:text-[18px] text-white/65 leading-[1.65] max-w-[52ch] mb-5">
                {currentAudience.subheadline}
              </p>

              {/* Trust strip — single-line authority anchor directly under
                  the subhead so cold visitors see credibility before the CTA. */}
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] sm:text-[12.5px] text-white/55 font-medium mb-7"
                data-testid="hero-trust-strip"
              >
                <span className="font-semibold text-white/75">Coach Varsay Lewis, M.Ed.</span>
                <span className="text-white/25">·</span>
                <span>2025 NJ State Championship staff</span>
                <span className="text-white/25 hidden sm:inline">·</span>
                <span className="hidden sm:inline">ELVTR Sports Psychology</span>
                <span className="text-white/25 hidden md:inline">·</span>
                <span className="hidden md:inline">300+ athletes coached</span>
              </div>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center">
                {isMember ? (
                  // Logged-in members skip the quiz CTA and go straight to their dashboard.
                  <Button
                    asChild
                    className="bg-[#004C54] hover:bg-[#003a40] text-white font-bold h-14 px-8 text-[16px] rounded-xl shadow-2xl shadow-[#004C54]/40 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200"
                  >
                    <a href="/app/dashboard" data-testid="hero-primary-cta" onClick={() => logCtaClick('hero-dashboard')}>
                      Go to your Dashboard
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                ) : (
                  <Button
                    asChild
                    className="bg-[#004C54] hover:bg-[#003a40] text-white font-bold h-14 px-8 text-[16px] rounded-xl shadow-2xl shadow-[#004C54]/40 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200"
                  >
                    <a href="/quiz" data-testid="hero-primary-cta" onClick={() => logCtaClick('hero-take-quiz')}>
                      Take the Free Mindset Quiz
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                )}

                {/* Secondary: Discovery Call — kept visible but visually subordinate
                    so the Quiz is the dominant primary action. */}
                <Button
                  asChild
                  variant="outline"
                  className="bg-transparent border-2 border-white/20 hover:border-white/40 text-white hover:bg-white/5 font-semibold h-14 px-7 text-[15px] rounded-xl transition-all duration-200"
                >
                  <a href={calendlyLink} data-testid="hero-secondary-cta" onClick={() => logCtaClick('hero-book-call')}>
                    <Phone className="h-4 w-4 mr-2" />
                    {isMember ? currentAudience.cta : 'Book a Discovery Call'}
                  </a>
                </Button>
              </div>

              {/* Microcopy under the CTAs — sets expectations for the quiz commitment. */}
              {!isMember && (
                <p className="mt-3 text-[12.5px] text-white/55 font-medium" data-testid="hero-microcopy">
                  Free · 2 minutes · Instant results · No credit card
                </p>
              )}

              {/* Tertiary low-commitment paths — Athlete IDP for buyers who already
                  know what they want. Members see "Manage subscription" instead. */}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13.5px]">
                {!isMember ? (
                  <a
                    href="/assessment"
                    data-testid="hero-assessment-cta"
                    onClick={() => logCtaClick('hero-assessment')}
                    className="inline-flex items-center gap-1.5 font-semibold text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-[#4FBCC7] transition-colors"
                  >
                    <Sparkles className="h-4 w-4 text-[#4FBCC7]" />
                    Or get the $27 Athlete IDP
                  </a>
                ) : (
                  <a
                    href="/app/pricing"
                    data-testid="hero-member-pricing-cta"
                    onClick={() => logCtaClick('hero-member-pricing')}
                    className="inline-flex items-center gap-1.5 font-semibold text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-[#4FBCC7] transition-colors"
                  >
                    <Sparkles className="h-4 w-4 text-[#4FBCC7]" />
                    Upgrade your membership
                  </a>
                )}
              </div>

              <div className="flex items-center gap-3 text-white/40 text-sm mt-5">
                <div className="h-px w-8 bg-white/20" />
                <span>300+ athletes coached · No-pressure 30-min discovery call</span>
              </div>

              {/* Phase 35 — front-desk alternative path. Non-members get a
                  low-key "or call" line under the trust strip; members
                  don't see it (they already have an account + dashboard). */}
              {!isMember && (
                <div className="mt-4">
                  <CallFrontDeskLine tone="dark" align="left" testId="hero-call-line" />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Logo — mirrors the OG / Twitter card energy: navy frame, layered teal glow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:flex justify-center items-center"
          >
            <div className="relative">
              {/* Layer 1 — top-left teal bloom */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-20 -left-20 w-[350px] h-[350px] rounded-full"
                style={{
                  background:
                    'radial-gradient(circle, rgba(79,188,199,0.80) 0%, rgba(79,188,199,0.50) 20%, rgba(79,188,199,0.20) 40%, transparent 65%)',
                  filter: 'blur(50px)',
                }}
              />
              {/* Layer 2 — bottom-right greener bloom (depth + motion) */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-24 -right-24 w-[400px] h-[400px] rounded-full"
                style={{
                  background:
                    'radial-gradient(circle, rgba(32,201,151,0.75) 0%, rgba(79,188,199,0.45) 25%, rgba(79,188,199,0.20) 45%, transparent 70%)',
                  filter: 'blur(60px)',
                }}
              />
              {/* Layer 3 — wide-soft outer halo */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 scale-[1.5]"
                style={{
                  background:
                    'radial-gradient(circle, rgba(79,188,199,0.70) 0%, rgba(79,188,199,0.40) 30%, rgba(79,188,199,0.15) 50%, transparent 75%)',
                  filter: 'blur(40px)',
                }}
              />
              {/* Layer 4 — tight inner halo (the "pop" right against the frame) */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 scale-[1.2]"
                style={{
                  background:
                    'radial-gradient(circle, rgba(79,188,199,0.90) 0%, rgba(79,188,199,0.50) 20%, transparent 50%)',
                  filter: 'blur(30px)',
                }}
              />

              {/* Logo frame — navy square w/ white logo, matches OG banner */}
              <div className="relative w-[300px] h-[300px] bg-[#004C54] rounded-md p-7 flex items-center justify-center shadow-[0_24px_64px_rgba(79,188,199,0.25)]">
                <div className="w-full h-full bg-white rounded-sm flex items-center justify-center p-3">
                  <img
                    src={LOGO_URL}
                    alt="Apex Athletic Mind — Mental Performance Coach for Competitive Athletes"
                    className="w-full h-full object-contain"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroWithTabs;
